import { z } from 'zod';
import { PropertyCategorySchema, PropertySubtypeSchema } from '../common/property-taxonomy.schema';
import { FontSourceSchema } from '../identity/workspace-branding.schemas';
import { AgencyImprintSchema } from '../deal/brokerage-agreement-page.schemas';
import {
  ConditionSchema,
  EnergyCertificateBasisSchema,
  EnergyCertificateYearSchema,
  ENERGY_CLASS_SCALES,
  EnergyClassSchema,
  FiringTypeSchema,
  HeatingTypeSchema,
  PropertyAmenitiesSchema,
} from './properties.schemas';
import { CommissionStatementSchema } from './buyer-commission';
import { TourProviderSchema } from './tours.schemas';
import { VideoLinkProviderSchema } from './video-links';

export const PublicWebExposePathParamsSchema = z.object({
  webAddress: z.string().min(1).max(64),
  objectNumber: z.string().min(1).max(16),
});

export const PublicWebExposePhotoSchema = z.object({
  url: z.string(),
  // The media derivative; null while it is not READY — the page then uses `url`.
  thumbnailUrl: z.string().nullable(),
  // The agent's own name for the photo, never an upload file name, never one that reveals a hidden street.
  caption: z.string().nullable(),
});

export const PublicWebExposeFloorPlanSchema = z.object({
  url: z.string(),
  thumbnailUrl: z.string().nullable(),
  // Attachment URL whose file name is „Grundriss {n}.{ext}", never the upload's name.
  downloadUrl: z.string(),
});

export const PublicWebExposeTourSchema = z.object({
  // The stored title, or „360°-Rundgang" when it is blank or reveals a hidden street (AC 21).
  title: z.string(),
  // Derived from `link` on read (IMM-1164.1 Decision 3); null for a host immoOS does not recognise — the page then names no provider.
  provider: TourProviderSchema.nullable(),
  // The stored https URL, opened at the provider in a new tab, never embedded.
  link: z.string(),
});
export type PublicWebExposeTour = z.infer<typeof PublicWebExposeTourSchema>;

// Two kinds, in the agent's Medien order: an uploaded file played in place, or a YouTube/Vimeo link
// opened at the provider, never embedded (IMM-1161.9, IMM-1165).
export const PublicWebExposeVideoSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('UPLOAD'),
    // Inline presigned URL of the original; the page loads it only on play (preload="none").
    url: z.string(),
    // The poster derivative; null while it is not READY.
    posterUrl: z.string().nullable(),
    // agentCaption, else „Video" — never a file name, never one that reveals a hidden street.
    title: z.string(),
  }),
  z.object({
    kind: z.literal('LINK'),
    provider: VideoLinkProviderSchema,
    // The page at the provider (`PropertyMediaVideoLink.url`), opened in a new tab, never embedded.
    link: z.string(),
    // immoOS's copy of the provider's preview image, inline; null when the file cannot be read.
    previewUrl: z.string().nullable(),
    // agentCaption, else „Video" — never one that reveals a hidden street.
    title: z.string(),
  }),
]);
export type PublicWebExposeVideo = z.infer<typeof PublicWebExposeVideoSchema>;

export const PublicWebExposeContactPersonSchema = z.object({
  name: z.string(),
  firstName: z.string().nullable(),
  position: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string(),
  photoUrl: z.string().nullable(),
});

export const PublicWebExposeFontSchema = z.object({
  family: z.string(),
  source: FontSourceSchema,
  // Google-sourced only: the stored weights. Empty for a curated family (bundled) and for a Google one never stored.
  files: z.array(z.object({ weight: z.string(), url: z.string() })),
});
export type PublicWebExposeFont = z.infer<typeof PublicWebExposeFontSchema>;

export const PublicWebExposeMapSchema = z.object({
  postalCode: z.string(),
  desktopUrl: z.string(),
  phoneUrl: z.string(),
  // Verbatim: the base map's attribution first, then LandIQ's.
  attributions: z.array(z.string()).min(1),
});
export type PublicWebExposeMap = z.infer<typeof PublicWebExposeMapSchema>;

export const PublicWebExposePageSchema = z.object({
  // The canonical link, the same the agent copies (`webExposeUrl`). „Teilen" shares it (IMM-1161.8).
  url: z.string(),
  agency: z.object({
    companyName: z.string().nullable(),
    logoUrl: z.string().nullable(),
    logoLightUrl: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    privacyPolicyUrl: z.string().nullable(),
    // null while the Firmenname is blank: an imprint without a firm name says nothing.
    imprint: AgencyImprintSchema.nullable(),
    branding: z.object({
      primaryColor: z.string().nullable(),
      inkColor: z.string().nullable(),
      headingFont: PublicWebExposeFontSchema.nullable(),
      bodyFont: PublicWebExposeFontSchema.nullable(),
    }),
  }),
  property: z.object({
    objectNumber: z.string(),
    // referenceNumber, else the Objekt-Nr. (PRD Detailed Business Rule 9)
    exposeNumber: z.string(),
    title: z.string(),
    category: PropertyCategorySchema,
    postalCode: z.string(),
    city: z.string(),
    district: z.string().nullable(),
    // Both null unless „Adresse anzeigen" was chosen at publication (AC 21).
    street: z.string().nullable(),
    houseNumber: z.string().nullable(),
    // null for „Preis auf Anfrage": `priceOnInquiry`, or no price stated.
    purchasePrice: z.number().nullable(),
    livingArea: z.number().nullable(),
    rooms: z.number().nullable(),
    subtype: PropertySubtypeSchema.nullable(),
    facts: z.object({
      bedrooms: z.number().nullable(),
      bathrooms: z.number().nullable(),
      usableArea: z.number().nullable(),
      plotArea: z.number().nullable(),
      floor: z.number().nullable(),
      floorsTotal: z.number().nullable(),
      yearBuilt: z.number().int().nullable(),
      condition: ConditionSchema.nullable(),
      lastRenovated: z.number().int().nullable(),
      availableFrom: z.string().nullable(),
      parkingSpaces: z.number().nullable(),
      // Hausgeld: SERVICE_CHARGE of an APARTMENT, as the OpenImmo `hausgeld` maps it.
      serviceCharge: z.number().nullable(),
    }),
    // de-DE only; blank → null.
    descriptions: z.object({
      description: z.string().nullable(),
      equipment: z.string().nullable(),
      location: z.string().nullable(),
      miscellaneous: z.string().nullable(),
    }),
    amenities: PropertyAmenitiesSchema,
    energy: z
      .object({
        certificateYear: EnergyCertificateYearSchema,
        certificateBasis: EnergyCertificateBasisSchema.nullable(),
        // The class on the Objekt's scale (classScale); null when unrecorded or off-scale.
        energyClass: EnergyClassSchema.nullable(),
        classScale: z.enum(ENERGY_CLASS_SCALES),
        energyValue: z.number().nullable(),
        primaryEnergyValue: z.number().nullable(),
        includesWarmWater: z.boolean(),
        certificateIssuedAt: z.string().nullable(),
        heatingType: z.array(HeatingTypeSchema),
        firingType: z.array(FiringTypeSchema),
      })
      .nullable(),
    commission: z.object({
      hasCommission: z.boolean(),
      // The legacy free text („3,57 %", „3,57 % inkl. MwSt."), null when blank. From IMM-1168 the page renders it only
      // while `statement` is null; kept on the wire so a cached older bundle never misreads a structured Objekt.
      rate: z.string().nullable(),
      // `commissionIncludesVat`: true means stated NET, VAT on top.
      net: z.boolean(),
      note: z.string().nullable(),
      // IMM-1168: the generated statement, or null while there is no complete figure.
      statement: CommissionStatementSchema.nullable(),
    }),
  }),
  photos: z.array(PublicWebExposePhotoSchema),
  floorPlans: z.array(PublicWebExposeFloorPlanSchema),
  videos: z.array(PublicWebExposeVideoSchema),
  // The published tours by position (IMM-1164 AC 4).
  tours: z.array(PublicWebExposeTourSchema),
  map: PublicWebExposeMapSchema.nullable(),
  contactPerson: PublicWebExposeContactPersonSchema.nullable(),
  fullExposeRequestOffered: z.boolean(),
});
export type PublicWebExposePage = z.infer<typeof PublicWebExposePageSchema>;
